import ConfirmDialog from '@/components/ConfirmDialog';
import Constants from '@/Constants';
import { Person } from '@/models/Person';
import { cpfMask } from '@/services/UtilService';
import { ApplicationState } from '@/store';
import { getPersonRequest, handlePersonClear, postPersonRequest, putPersonRequest } from '@/store/person/action';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { formInitialState, schema } from './constants';
import './style.scss';

const FormPerson: React.FC = () => {
    const history = useHistory();
    const dispatch = useDispatch();

    const { error, hasError, loading, personState, strings, identity, modules } = useSelector((state: ApplicationState) => {
        return {
            error: state.people.error,
            hasError: state.people.hasError,
            loading: state.people.loading,
            personState: state.people.person,
            strings: state.locale.data,
            identity: state.identity.data,
            modules: state.appOrganization.data.modules,
        };
    });

    const { id } = useParams<{ id: string }>();

    const [person, setPerson] = useState<Person | null>();
    const [formState, setFormState] = useState(formInitialState);
    const { fullName, email, position, cpf, departmentName } = formState;
    const [isFormInvalid, setIsFormInvalid] = useState(true);

    const handleOnChange = useCallback((fieldName: string, value: any) => {
        if (!value) value = '';
        setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
    }, []);

    const handleSubmit = () => {
        const {
            user: { orgId, applicationId },
        } = identity;

        let _person: Person | undefined;
        if (!person) {
            _person = {
                id: undefined,
                orgId,
                applicationId,
                locale: 'pt',
                registration: '',
                firstName: fullName.split(' ')[0],
                lastName: fullName.replace(fullName.split(' ')[0], '').trim(),
                email,
                departmentName,
                position,
                alsoUpdateUserLogin: true,
                cpf: cpf.replace(/\D/g, ''),
                moduleIds: modules.map((module) => module.id),
            };
        } else {
            _person = {
                ...person,
                alsoUpdateUserLogin: true,
                orgId,
                applicationId,
                firstName: fullName.split(' ')[0],
                lastName: fullName.replace(fullName.split(' ')[0], '').trim(),
                email,
                departmentName,
                position,
                cpf: cpf.replace(/\D/g, ''),
            };
        }

        if (_person && !_person.id) {
            dispatch(postPersonRequest({ person: _person, history, willClose: true }));
        } else {
            dispatch(putPersonRequest({ person: _person, history, willClose: true }));
        }
    };

    useEffect(() => {
        if (personState) {
            const _person = personState.person;
            setPerson(_person);
            handleOnChange('fullName', `${_person.firstName} ${_person.lastName}`);
            handleOnChange('email', _person.email);
            handleOnChange('position', _person.position);
            handleOnChange('cpf', cpfMask(_person.cpf));
            handleOnChange('departmentName', _person.departmentName);
        } else {
            setFormState(formInitialState);
        }
    }, [personState]);

    useEffect(() => {
        if (id) {
            dispatch(getPersonRequest(Number(id)));
        } else {
            setPerson(null);
            setFormState(formInitialState);
        }
    }, [id]);

    useEffect(() => {
        schema
            .validate(formState)
            .then(() => setIsFormInvalid(false))
            .catch(() => setIsFormInvalid(true));
    }, [formState]);

    const contentErrorMessage = (
        <span dangerouslySetInnerHTML={{ __html: strings.GENERIC_CONTACT_SUPPORT.replace('%s', error?.response?.status.toString() || '511') }} />
    );

    const onOkHandler = () => {
        dispatch(handlePersonClear());
    };

    return (
        <>
            {loading && <h1>{strings.LOADING}</h1>}
            <ConfirmDialog
                content={contentErrorMessage}
                icon={Constants.icons.REPORT_PROBLEM}
                rightButtonTitle={strings.I_GOT_IT}
                rightClick={onOkHandler}
                show={hasError}
                title={strings.ERROR_REGISTER}
            />
            {!loading && (
                <div className="create-person">
                    <div className="back-button go-back navbar navbar-expand sub-nav" data-target="#alert-modal" data-toggle="modal">
                        <span className="container" onClick={() => history.goBack()} onKeyDown={() => {}} role="button" tabIndex={0}>
                            <i className="material-icons-outlined align-middle">keyboard_arrow_left</i>
                            <span>{strings.GO_BACK}</span>
                        </span>
                    </div>
                    <div className="content container">
                        <div className="title">
                            <h1>{strings.USER_REGISTRATION}</h1>
                        </div>
                        <span className="warning-text">
                            {Constants.symbol.ASTERISK}
                            {strings.REQUIRED_FIELDS}
                        </span>
                        <div className="form-group form-person container">
                            <div className="row input-section">
                                <div className="col">
                                    <div className="title-container input-required-asterisk">
                                        <h1>
                                            {strings.FULL_NAME} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                                        </h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        onChange={(e) => handleOnChange('fullName', e.target.value)}
                                        placeholder={strings.TYPE_FULL_NAME}
                                        required
                                        value={fullName}
                                    />
                                </div>
                            </div>

                            <div className="row input-section">
                                <div className="col">
                                    <div className="title-container">
                                        <h1>
                                            {strings.YOUR_EMAIL} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                                        </h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        onChange={(e) => handleOnChange('email', e.target.value)}
                                        placeholder={strings.TYPE_EMAIL}
                                        required
                                        value={email}
                                    />
                                </div>
                            </div>

                            <div className="row input-section">
                                <div className="col">
                                    <div className="title-container">
                                        <h1>
                                            {strings.POSITION} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                                        </h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        onChange={(e) => handleOnChange('position', e.target.value)}
                                        placeholder={strings.TYPE_POSITION}
                                        required
                                        value={position}
                                    />
                                </div>
                            </div>

                            <div className="row input-section">
                                <div className="col">
                                    <div className="title-container">
                                        <h1>
                                            {strings.DEPARTMENT} <span className="input-required-asterisk">{Constants.symbol.ASTERISK}</span>
                                        </h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        onChange={(e) => handleOnChange('departmentName', e.target.value)}
                                        placeholder={strings.TYPE_DEPARTMENT}
                                        required
                                        value={departmentName}
                                    />
                                </div>
                                <div className="col">
                                    <div className="title-container">
                                        <h1>CPF</h1>
                                    </div>
                                    <input
                                        className="form-control"
                                        maxLength={14}
                                        onChange={(e) => handleOnChange('cpf', cpfMask(e.target.value))}
                                        placeholder={strings.TYPE_CPF}
                                        value={cpf}
                                    />
                                </div>
                            </div>

                            <div className="d-flex justify-content-end submit-buttons">
                                <div>
                                    <button className="btn btn-link text-uppercase" onClick={() => history.goBack()} type="button">
                                        {strings.CANCEL}
                                    </button>
                                    <button
                                        className="btn btn-primary btn-sm text-uppercase"
                                        disabled={isFormInvalid}
                                        onClick={handleSubmit}
                                        type="button"
                                    >
                                        {strings.SAVE}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FormPerson;
